import React, { memo, useMemo, useEffect, useState } from "react";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import { Button } from "components";
import { saveDesign, updateDesign } from "services/headwallServices";
import {
  errorAlert,
  successAlert,
  warningAlert,
} from "config/sweet-alert/alert";
import { PRODUCT } from "constants/appConstant";
import {
  useLoaderStore,
  useServiceStore,
  useHeadWallStore,
  useRightPanelStore,
} from "store";
import queryClient from "services/queryClient";

function RightPanel({ children, handleGenerateImage, designId, designName }) {
  const { setLoading } = useLoaderStore();
  const { serv, view_only, setService } = useServiceStore();
  const { values, setValues, resetState } = useHeadWallStore();
  const { rightPanelStepper, updateStepCompletion } = useRightPanelStore();
  const [readyToGenerate, setReadyToGenerate] = useState(false);

  const navigate = useNavigate();

  const {
    width,
    ceiling_height,
    model,
    headwalls,
    name,
    sub_model,
    step,
    chase_position,
    chased,
    project_name,
    location,
    cad_requested,
    quote_requested,
    accessory_rails,
  } = values;

  const createDesignMutation = useMutation(saveDesign, {
    onSuccess: (data) => {
      successAlert("Ok", data?.data?.message, handleConfirm);
    },
    onError: (err) => {
      setLoading(false);
      errorAlert(err.response.data.message);
    },
  });

  const updateDesignMutation = useMutation(updateDesign, {
    onSuccess: (data) => {
      setLoading(false);
      successAlert("Ok", data?.data?.message);
      resetState();
      queryClient.removeQueries(["designs"]);
      navigate("/dashboard");
    },
    onError: (err) => {
      setLoading(false);
      errorAlert(err.response.data.message);
    },
  });

  useEffect(() => {
    if (readyToGenerate) {
      (async () => {
        await handleGenerateImage();
        // remove condition after accessorie implementation for rest of the headwalls
        if (
          model === PRODUCT.INFINITY ||
          model === PRODUCT.AXIOM ||
          model === PRODUCT.ARRAY ||
          model === PRODUCT.COMPASS
        )
          handleStep(step + 1);
        else handleStep(step + 2);
        updateStepCompletion(6, true);
        setReadyToGenerate(false); // reset flag
      })();
    }
  }, [serv, readyToGenerate]);

  //manage loader
  useEffect(() => {
    setLoading(
      createDesignMutation.isLoading || updateDesignMutation.isLoading
    );
    return () => {
      setLoading(false);
    };
  }, [
    setLoading,
    createDesignMutation.isLoading,
    updateDesignMutation.isLoading,
    view_only,
  ]);

  const headwallData = useMemo(() => {
    const transformedData = {
      headwalls: headwalls.map((headwall) => {
        return {
          width:
            model === PRODUCT.COMPASS ? headwall.width : headwall.width * 12,
          position: headwall?.position,
          color: headwall?.color,
          background: headwall?.background,
          services: headwall.services.map((service) => {
            return {
              service_type_id: service._id,
              name: service.name,
              rowPosition: service?.rowPosition || "",
              x_distance_inches: service.x_distance_inches,
              y_distance_inches: service.y_distance_inches,
              service_item_id: service?.specification?.service_item_id,
              custom_specifications:
                service?.specification?.custom_specification || "",
              is_slide: service?.is_slide || false,
              u_id: service.u_id,
              positions: service.positions,
              manufacturer_name: service?.specification?.manufacturer,
              specification_name: service?.specification?.specification,
              category_name: service.category,
            };
          }),
        };
      }),

      directAccesiories: accessory_rails["direct"].map((service) => {
        if (service?.is_slide)
          return {
            service_type_id: service._id,
            name: service.name,
            rowPosition: service?.rowPosition || "",
            x_distance_inches: service.x_distance_inches,
            y_distance_inches: service.y_distance_inches,
            service_item_id: service?.specification?.service_item_id,
            custom_specifications:
              service?.specification?.custom_specification || "",
            is_slide: service?.is_slide || false,
            u_id: service.u_id,
            positions: service.positions,
            manufacturer_name: service?.specification?.manufacturer,
            specification_name: service?.specification?.specification,
            category_name: service.category,
          };
        else return service;
      }),
    };
    return transformedData;
  }, [headwalls, model,accessory_rails]);

  const handleConfirm = () => {
    resetState();
    queryClient.removeQueries(["designs"]);
    navigate("/dashboard");
  };

  const handleStep = (step) => {
    setValues({ step: step });
  };

  const handleContinue = async () => {
    if (checkValidation()) {
      if (model !== PRODUCT.AXIOM && step === 1) {
        handleStep(step + 2);
        updateStepCompletion(3, true);
      } else if (
        (model === PRODUCT.AXIOM ||
          model === PRODUCT.CONSOLE ||
          model === PRODUCT.ELOQUENCE ||
          model === PRODUCT.SECURITY ||
          model === PRODUCT.HIDDEN) &&
        step <= 2
      ) {
        handleStep(step + 1);
        updateStepCompletion(3, true);
      } else if (step === 3) {
        if (model === PRODUCT.INFINITY && chased && chase_position === "no")
          warningAlert(
            "Hey, you forgot to pick the chase before moving forward with your design."
          );
        else {
          handleStep(step + 1);
          updateStepCompletion(4, true);
        }
      } else if (step === 4) {
        // temp condition
        if (
          model === PRODUCT.INFINITY ||
          model === PRODUCT.AXIOM ||
          model === PRODUCT.ARRAY ||
          model === PRODUCT.COMPASS
        ) {
          handleStep(step + 1);
          updateStepCompletion(5, true);
        }
        // remove this code after accessorie implementation on other headwalls
        else {
          setService("", false); // Triggers update
          setReadyToGenerate(true);
        }
        // ----------------------------------//
      } else if (step === 5) {
        setService("", false); // Triggers update
        setReadyToGenerate(true);
      } else if (step === 6) {
        const user = localStorage.getItem("user");
        if (user) {
          const newValues = { ...values };
          delete newValues.step;
          delete newValues?.is_template;
          delete newValues?.in_folder;
          accessory_rails["direct"] = headwallData.directAccesiories;
          if (!designId || designName !== name.trim()) {
            delete newValues?._id;
            createDesignMutation.mutate({
              ...newValues,
              name: name.trim(),
              model: model,
              width: Number(width.ft * 12 + width.in),
              ceiling_height: Number(
                ceiling_height.ft * 12 + ceiling_height.in
              ),
              headwalls: headwallData.headwalls,
              accessory_rails: accessory_rails,
            });
          } else {
            const updatedValues = {
              ...newValues,
              name: name.trim(),
              model: model,
              width: Number(width.ft * 12 + width.in),
              ceiling_height: Number(
                ceiling_height.ft * 12 + ceiling_height.in
              ),
              headwalls: headwallData.headwalls,
              accessory_rails: accessory_rails,
            };
            updateDesignMutation.mutate({ updatedValues, designId });
          }
        } else navigate("/");
      }
    }
  };

  const handleBack = () => {
    if (
      (model === PRODUCT.INFINITY ||
        model === PRODUCT.ARRAY ||
        model === PRODUCT.COMPASS) &&
      step === 3
    )
      handleStep(step - 2);
    else if (
      (model === PRODUCT.CONSOLE ||
        model === PRODUCT.ELOQUENCE ||
        model === PRODUCT.HIDDEN ||
        model === PRODUCT.SECURITY) &&
      step === 6
    )
      handleStep(step - 2);
    else {
      if (step === 2) resetState();
      handleStep(step - 1);
    }
  };

const handleClick = async (st) => {
  if (!st.completed) return;

  // Block step 5 for certain models
  if (
    (model === PRODUCT.CONSOLE ||
      model === PRODUCT.ELOQUENCE ||
      model === PRODUCT.SECURITY ||
      model === PRODUCT.HIDDEN) &&
    st.step === 5
  ) {
    return; // do nothing
  }

  if (st.step === 6) {
    await handleGenerateImage();
  }

  handleStep(st.step);
};


  const buttonText = useMemo(() => {
    return step <= 4 ? "Continue" : step === 5 ? "Preview" : "Save Design";
  }, [step]);

  const checkValidation = () => {
    let isValid = true;
    if (
      model !== PRODUCT.COMPASS &&
      model !== PRODUCT.ARRAY &&
      step === 3 &&
      !width.ft &&
      width.in < 5
    ) {
      if (!width.ft) {
        setValues({
          ...values,
          width: {
            ...values["width"],
            ftError: "Minimum Width should be 5 inches.",
          },
        });
      }
      isValid = false;
    }
    return isValid;
  };

  const isDisabled = () => {
    if (step === 2 && !sub_model) {
      return true;
    } else if (
      step === 6 &&
      (!name.trim() ||
        !project_name.trim() ||
        (!location.trim() && (cad_requested || quote_requested)))
    )
      return true;
    else return false;
  };

  return (
    <>
      <div className="right_panel">
        <div className={"right_panel-inner rounded-b-2xl mb-4"}>
          <div className="flex flex-col justify-between p-[23px] h-full ">
            <div className="h-full">
              {step > 2 && (
                <div className="flex justify-between mb-4 overflow-auto">
                  {rightPanelStepper.map((s, i) => (
                    <div
                      key={i}
                      className={`flex flex-col border justify-center items-center py-2 px-4 rounded bg-white right_panel_step_box_active font-normal h-[67px] w-[70px] ${
                        step === s.step ? "opacity-100" : "opacity-50"
                      } ${
                        s.completed ? "cursor-pointer " : "cursor-not-allowed"
                      }`}
                      onClick={() => handleClick(s)}
                    >
                      <img src={s.icon} alt="...img" />
                      <p className={`mt-1 !text-[11px]`}>{s.text}</p>
                    </div>
                  ))}
                </div>
              )}
              <div>{children}</div>
            </div>
          </div>
        </div>
        {step > 1 && (
          <div
            className={`double_button mx-4 ${serv && step === 4 && "hidden"}`}
          >
            <Button
              className="btnTxt backButton border-2   bg-white font-semibold py-2 px-4 rounded cursor-pointer"
              text={"Back"}
              handleClick={handleBack}
              type={"button"}
            />
            <Button
              className="login_btn py-2 px-4 w-full text-white cursor-pointer rounded btnTxt"
              text={buttonText}
              handleClick={() => {
                handleContinue();
              }}
              isDisabled={isDisabled()}
              type={"submit"}
            />
          </div>
        )}
        {step === 1 && designId && (
          <div className="single_button mx-4">
            <Button
              className="login_btn py-2 px-4 w-full text-white cursor-pointer rounded btnTxt"
              text={"Continue >>"}
              handleClick={() => {
                handleContinue();
              }}
              isDisabled={model ? false : true}
              type={"button"}
            />
          </div>
        )}
      </div>
    </>
  );
}

export default memo(RightPanel);
